import { notifyOnError } from '~/rage/decorators';
import {
	CharacterLicense,
	IVehicleLastOwnersHistory,
	IVehiclePassportDetails,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import {
	IVehiclePassport,
	IVehiclePassportService,
} from './vehiclePassportServiceTypes';

export class VehiclePassportService implements IVehiclePassportService {
	constructor(
		private readonly gameConfig: GameConfigService,
		private readonly vars: VarsService,
		private readonly backend: BackendService,
		private readonly rpc: RpcService,
	) {}

	@notifyOnError
	public async getPassportAndHistory(): Promise<{
		passport: IVehiclePassport | null;
		lastOwnersHistory: IVehicleLastOwnersHistory[];
	}> {
		const vehicleId = this.vars
			.get<string | null>('vehiclePassportVehicleId', null)
			.getValue();
		if (!vehicleId) {
			throw new Error('Failed to obtain vehicle id for vehicle passport');
		}

		const { vehicleModel } = await this.backend.invoke<{
			vehicleModel: string | null;
		}>('vehicles:getModelById', { vehicleId });

		const modelConfig = this.gameConfig.configs?.vehicles?.models?.find(
			(e) => e.model === vehicleModel,
		);

		if (!modelConfig) {
			throw new Error(
				`Failed to obtain vehicle model config for ${vehicleModel} in vehicle passport`,
			);
		}

		const [passportDetails, lastOwnersHistory, licenseCategory] =
			await Promise.all([
				this.backend.invoke<IVehiclePassportDetails>(
					'vehicles:getPassportDetails',
					{
						vehicleId,
					},
				),
				this.backend.invoke<IVehicleLastOwnersHistory[]>(
					'vehicles:getLastOwnersHistory',
					{
						vehicleId,
					},
				),
				this.rpc.invokeClient<CharacterLicense>(
					'vehiclePassport:getLicenseCategoryByVehicleModel',
					{
						vehicleModel,
					},
				),
			]);

		const { vinNumber, docNumber, duplicate } = passportDetails;
		const { name, brandName, modelName, bodyType, enginePower } = modelConfig;
		const ownersCount = lastOwnersHistory.length;
		const ownerNickname =
			lastOwnersHistory[lastOwnersHistory.length - 1].ownerNickname;

		const passport = {
			name,
			brandName,
			modelName,
			bodyType,
			licenseCategory,
			ownersCount,
			enginePower,
			registryCityName: 'г. Лос-Сантос',
			ownerNickname,
			registryPlaceName: 'п. Департамент Лос-Сантоса',
			vinNumber,
			docNumber,
			duplicate,
		};

		return {
			passport,
			lastOwnersHistory,
		};
	}

	@notifyOnError
	public async exit(): Promise<void> {
		return await this.rpc.invokeClient('vehiclePassport:exit');
	}
}
